import { useState } from 'react';
import { useSaveForm } from './useSaveForm';

/**
 * State of a detail (create / edit) form: values, server field errors and the "saved" flag.
 * `setField` clears the server error of the changed field (or of `errorKey` when names differ).
 */
export function useEntityForm<V extends object, R>(
  initial: V,
  submit: (values: V) => Promise<R>,
  onSuccess?: (result: R) => void,
) {
  const [values, setValues] = useState<V>(initial);
  const [saved, setSaved] = useState(false);

  const form = useSaveForm(submit, (result) => {
    setSaved(true);
    onSuccess?.(result);
  });

  const setField = <K extends keyof V>(field: K, value: V[K], errorKey: string = String(field)) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
    form.clearErrors(errorKey);
  };

  return {
    values,
    setField,
    saved,
    errors: form.errors,
    isSaving: form.isPending,
    save: () => form.save(values),
  };
}
